import { EventListener, EventEmitter, View, ViewData, CollectionView } from "silver";
import { BooksLang } from "Lang";
import { Book as BookData } from "core/Data/Book";

export interface BookControlMetric<Type = any> {
	label: string;
	value: (book: BookData) => Type;
	valueFormatted: (value: Exclude<Type, null>, book: BookData) => string|Node;
}

export interface BookMetricViewData extends ViewData {
	metric: BookControlMetric<any>;
}

export interface BookControlViewData extends ViewData {
	lang: { [ key: string ]: string }|BooksLang;
	listMetrics: BookMetricViewData[];
	listLabels: BookMetricViewData[];
	isOnMyList: boolean;
	checkbox: boolean;
	row: boolean;
	card: boolean;
	lengthClass: string;
	bookId: number;
	langString: string;
}

export interface BookControlView<Data extends BookControlViewData> extends View<Data> {
	cover: HTMLDivElement;
	listMetrics: CollectionView<BookMetricViewData, View<BookMetricViewData>>;
	listLabels: CollectionView<BookMetricViewData, View<BookMetricViewData>>;
	img: HTMLImageElement;
	imgRatioFix: HTMLDivElement;
	checkbox: HTMLInputElement;
}

export enum BookControlViewMode {
	CARD = "card",
	ROW = "row"
}

function getDefaultCoverSize(viewMode: BookControlViewMode): { width: number, height: number } {
	if (viewMode === BookControlViewMode.CARD) {
		return { width: 170, height: 180 };
	}
	return { width: 98, height: 100 };
}

export interface BookControl {
	readonly onCheckChange: EventEmitter<{ sender: BookControl }>;
	readonly onMyListChange: EventEmitter<{ sender: BookControl }>;
	readonly id: number;
	readonly element: HTMLElement;
	readonly cover: HTMLElement;
	checked: boolean;
	onMyList: boolean;
	data?: BookData;

	unset(): void;
	setCheckboxAvailability(available: boolean, describedById?: string): void;
	setViewMode(viewMode: BookControlViewMode): void;
	setListMetrics(metrics: Array<BookControlMetric<any>>): void;
	setListLabels(labels: Array<BookControlMetric<any>>): void;
}

export abstract class AbstractBookControl implements BookControl {

	public readonly onCheckChange = EventListener.create<{ sender: BookControl }>("checkChange");
	public readonly onMyListChange = EventListener.create<{ sender: BookControl, source: HTMLElement }>("myListChange");

	protected bookData?: BookData;
	protected bookLang?: BooksLang;
	protected abstract view: BookControlView<any>;
	protected viewMode: BookControlViewMode = BookControlViewMode.CARD;

	protected constructor(public readonly id: number) { }

	public setListMetrics(listMetrics: Array<BookControlMetric<any>>): void {
		this.view.listMetrics.empty();
		this.view.set({ listMetrics: listMetrics.map(metric => ({ metric })) });
	}

	public setListLabels(listLabels: Array<BookControlMetric<any>>): void {
		this.view.listLabels.empty();
		this.view.set({ listLabels: listLabels.map(metric => ({ metric })) });
	}

	public get element(): HTMLElement {
		return this.view.element;
	}

	public get cover(): HTMLElement {
		return this.view.cover;
	}

	protected setOnMyList(onMyList: boolean, sender?: any, source?: HTMLElement): void {
		if (this.view.dataset.isOnMyList === onMyList) {
			return;
		}
		this.view.dataset.isOnMyList = onMyList;
		if (sender === this && source) {
			this.onMyListChange.trigger({ sender: this, source });
		}
	}

	public get onMyList(): boolean {
		return this.view.dataset.isOnMyList;
	}

	public set onMyList(onMyList: boolean) {
		this.setOnMyList(onMyList);
	}

	protected updateCoverURL(): void {
		if (!this.bookData) {
			const { width, height } = getDefaultCoverSize(this.viewMode);
			this.view.img.setAttribute("height", `${width}px`);
			this.view.img.setAttribute("width", `${height}px`);
			this.view.imgRatioFix.style.paddingTop = (100 * height / width) + "%";
			return;
		}
		this.view.img.onload = () => {
			this.view.img.removeAttribute("height");
			this.view.img.removeAttribute("width");
			this.view.imgRatioFix.style.paddingTop = (100 * this.view.img.naturalHeight / this.view.img.naturalWidth) + "%";
		};
		this.view.img.src = this.bookData.coverUrl;
	}

	public setLang(bookLang: BooksLang): void {
		this.bookLang = bookLang;
		this.view.set({ lang: bookLang });
		this.view.invalidate("isOnMyList");
		this.view.listMetrics.invalidate("metric");
	}

	get checked(): boolean {
		return this.view.checkbox.checked;
	}

	set checked(checked: boolean) {
		this.view.checkbox.checked = checked;
	}

	public setCheckboxAvailability(available: boolean, describedby?: string): void {
		this.view.set({ checkbox: available });
		if (describedby) {
			this.view.checkbox.setAttribute("aria-describedby", describedby);
		}
	}

	public setViewMode(viewMode: BookControlViewMode): void {
		this.viewMode = viewMode;
		this.view.set({
			row:  viewMode === BookControlViewMode.ROW,
			card: viewMode === BookControlViewMode.CARD
		});
		this.updateCoverURL();
	}

	public unset(): void {
		if (this.view.element.parentNode) {
			this.view.element.parentNode.removeChild(this.view.element);
		}
		this.onCheckChange.clear();
	}
}
